import React, { useState } from 'react'
import clsx from 'clsx'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
type AggKey = 'value' | 'cogs' | 'soldAmount'


type AggProps = {
  slug: AggKey
  label: string
  title: string
}

const aggs: AggProps[] = [
  { label: 'Qty', slug: 'value', title: 'Sold count' },
  { label: 'COGS', slug: 'cogs', title: 'COGS' },
  { label: 'Amount', slug: 'soldAmount', title: 'Sold amount' },
]

type Props<T> = {
  id: string
  title: React.ReactNode
  data: T[]
  height: number
  isLoading: boolean
  getLabel: (item: T) => string
  getColorClass?: (item: T) => string
  showAggs?: boolean
  getAggValue: (item: T, agg: AggKey) => number
}

export function TreemapCard<T>({
  id,
  title,
  data,
  height,
  isLoading,
  getLabel,
  getColorClass,
  getAggValue,
  showAggs = false,
}: Props<T>) {
  const [agg, setAgg] = useState<AggKey>('value')
  const currentAgg = showAggs ? agg : 'value'
  const sortedData = [...data].sort((a, b) => {
    return getAggValue(b, currentAgg) - getAggValue(a, currentAgg)
  })

  const fallback = [
    '#009ef7',
    '#50cd89',
    '#ffc700',
    '#f1416c',
    '#7239ea',
    '#181c32',
    '#47be7d',
    '#f1bc00',
    '#d9214e',
    '#5014d0',
    '#0095e8',
    '#e1e3ea',
    '#b5b5c3',
  ]

  const colors = data.map((item, i) => getColorClass?.(item) || fallback[i % fallback.length])

  const series = [{
    data: sortedData.map((item) => ({
      x: getLabel(item),
      y: getAggValue(item, currentAgg)
    }))
  }]

  const options: ApexOptions = {
    chart: {
      id,
      toolbar: { show: false },
    },
    colors,
    legend: {
      show: false
    },
    plotOptions: {
      treemap: {
        distributed: true,
        enableShades: false,
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => {
          if (['cogs', 'soldAmount'].includes(currentAgg)) {
            return `${val.toFixed(2)} €`
          }
          return val.toString()
        }
      }
    }
  }

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title">
          <span className="card-label fw-bold fs-3 mb-1">{title}</span>
        </div>
        {showAggs && (
          <div className="pb-5 mt-5">
            {aggs.map((p) => (
              <button
                key={p.slug}
                className={clsx(
                  'btn btn-sm btn-color-muted btn-active btn-active-primary me-3',
                  { active: agg === p.slug }
                )}
                type="button"
                title={p.title}
                onClick={() => setAgg(p.slug)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="card-body p-0 m-0">
        {isLoading ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <i className="spinner-border fs-5x" />
          </div>
        ) : data.length === 0 ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <span>No data</span>
          </div>
        ) : (
          <div className="row align-items-center mx-0 w-100">
            <Chart options={options} series={series} type="treemap" height={height} />
          </div>
        )}
      </div>
    </div>
  )
}
